import { usePayrollFlow } from '@/pages/payroll/use-payroll-flow';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@seapoint/client/src/design-system/button/button';
import { Stack } from '@seapoint/client/src/design-system/stack/stack';
import { PayrollEvidenceLineItemView, PayrollRecordView, PayrollView } from '@seapoint/client/src/types/entity-types';
import { LegalEntityCountry } from '@seapoint/server/enums';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import z from 'zod';
import { getAmountWithCurrency } from '../../../../../utils/get-amount-with-currency';
import { PayrollEmployee } from '../../financial-information-step/financial-information-table-column-definition';
import { RecordsTable } from './records-table';

const payrollEmployeeSchema = z.object({
  employees: z.array(
    z.object({
      id: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      destination: z
        .object({
          iban: z.string().nullable().optional(),
          accountNumber: z.string().nullable().optional(),
          sortCode: z.string().nullable().optional(),
          type: z.string().nullable().optional(),
        })
        .nullable()
        .optional(),
      reference: z.string().min(1, 'Reference is required'),
      netPay: z.string(),
    }),
  ),
});

export type PayrollFormData = z.infer<typeof payrollEmployeeSchema>;

type NetPayFormProps = {
  payrollFlow: ReturnType<typeof usePayrollFlow>;
  evidenceItems: PayrollEvidenceLineItemView[];
  currentPayroll: PayrollView;
  legalEntityCountry: LegalEntityCountry;
  isSubmitting: boolean;
  onContinue: (
    formData: PayrollFormData['employees'],
    payrollRecords: PayrollRecordView[],
    recordsToDelete: string[],
  ) => void;
};

export const NetPayForm = ({
  payrollFlow,
  evidenceItems = [],
  currentPayroll,
  legalEntityCountry,
  isSubmitting,
  onContinue,
}: NetPayFormProps) => {
  const getFormValues = useCallback(
    () => ({
      employees: currentPayroll?.records.map((emp) => {
        const evidenceNetPay = evidenceItems?.find((lineItem) => lineItem.employeeId === emp.employee.id)?.netPay;
        return {
          id: emp.employee.id,
          firstName: emp.employee.firstName,
          lastName: emp.employee.lastName,
          destination: emp.employee.destination,
          netPay: String(evidenceNetPay || emp.netPay),
          reference: currentPayroll?.reference ?? '',
        };
      }),
    }),
    [currentPayroll, evidenceItems],
  );

  useEffect(() => {
    if (currentPayroll.records.length === 0) return;
    setSelectedRowIds(currentPayroll.records.map((emp) => emp.employeeId) || []);
  }, [currentPayroll.records]);

  const form = useForm<PayrollFormData>({
    resolver: zodResolver(payrollEmployeeSchema),
    defaultValues: getFormValues(),
  });

  useEffect(() => {
    form.reset(getFormValues());
  }, [getFormValues, form]);

  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);

  const { watch, setValue, handleSubmit } = form;

  const watchedEmployees = watch('employees') as PayrollEmployee[];

  const totalNetPay = watchedEmployees.reduce((acc, employee) => {
    const isSelectedRecord = selectedRowIds.includes(employee.id);
    if (!isSelectedRecord) return acc;

    const isValidNetPay = employee.netPay && employee.netPay !== '';
    if (!isValidNetPay) return acc;

    return acc + Number(employee.netPay);
  }, 0);

  const formattedTotalNetPay = getAmountWithCurrency({
    amount: totalNetPay,
    currency: payrollFlow?.currentPayroll?.currency,
    isAccountBalance: true,
  });

  const onSubmitForm = async (formData: PayrollFormData) => {
    const payrollRecords = currentPayroll.records;
    const validFormRecordsWithNetPay = formData.employees
      .filter((emp) => selectedRowIds.includes(emp.id))
      .filter((emp) => Number(emp.netPay) > 0);
    const validPayrollRecords =
      payrollRecords?.filter((record) => validFormRecordsWithNetPay.find((emp) => emp.id === record.employeeId)) || [];
    const recordsToDelete =
      payrollRecords?.filter((a) => !validPayrollRecords.find((b) => b.id === a.id)).map((c) => c.id) || [];
    onContinue(validFormRecordsWithNetPay, validPayrollRecords, recordsToDelete);
  };

  const handleNetPayChange = (employeeId: string, netPay: string) => {
    const isSelectedRecord = selectedRowIds.includes(employeeId);
    if (!isSelectedRecord) {
      setSelectedRowIds([...selectedRowIds, employeeId]);
    }
    const employeeIndex = watchedEmployees.findIndex((emp) => emp.id === employeeId);
    if (employeeIndex !== -1) {
      setValue(`employees.${employeeIndex}.netPay`, netPay, { shouldValidate: true });
    }
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmitForm)}>
        <Stack space={3} align="stretch">
          <RecordsTable
            records={watchedEmployees}
            selectedRowIds={selectedRowIds}
            setSelectedRowIds={setSelectedRowIds}
            legalEntityCountry={legalEntityCountry}
            onNetPayChange={handleNetPayChange}
            showNetPay
          />
          <p className="mt-4 w-full pr-4 text-right text-sm">{formattedTotalNetPay}</p>
          <Button type="submit" className="w-full" variant="default" isLoading={isSubmitting} disabled={isSubmitting}>
            Continue
          </Button>
        </Stack>
      </form>
    </FormProvider>
  );
};
